import {ref, computed} from 'vue'
import {authStore} from '@/composables/store-accessor'
import {useNuxtApp} from '#app' // For $toast

// Define the interface for ticket types (can be moved to a shared types file)
export interface TicketType {
    id: string
    name: string
    category?: string
}

function persistConfigToMongo(key: string, value: string) {
    try {
        const token = useCookie('token').value
        if (token) {
            fetch('/proxy/messaging/config', {
                method: 'POST',
                headers: {'Content-Type': 'application/json', Token: token},
                body: JSON.stringify({key, value}),
            }).catch(err => console.error(`Error persisting ${key} to MongoDB:`, err))
        }
    } catch (err) {
        console.error(`Error persisting ${key} to MongoDB:`, err)
    }
}

export function useTicketTypes() {
    const nuxtApp = useNuxtApp()

    const ticketTypes = ref<TicketType[]>([])
    const isLoadingTicketTypes = ref(false)
    const ticketTypesError = ref<string | null>(null)
    const isCreatingTicketTypes = ref(false)
    const isUpdatingTicketTypes = ref(false)
    const updateResults = ref({success: 0, failed: 0})

    const hotelierTicketTypeIds = ref<string[]>([])
    const customerTicketTypeIds = ref<string[]>([])

    const isTO1User = computed(() => authStore.isTO1)

    async function loadTicketTypes() {
        isLoadingTicketTypes.value = true
        ticketTypesError.value = null

        try {
            const token = useCookie('token').value
            if (!token) {
                throw new Error('Authentication token not found. Please log in.')
            }

            const response = await fetch('/proxy/tickets/types', {
                headers: {
                    Accept: 'application/json',
                    Token: token,
                },
            })

            if (!response.ok) {
                let errorMessage = `Error: ${response.status}`
                throw new Error(errorMessage)
            }

            const data = await response.json()

            if (data && data.ticketTypes && Array.isArray(data.ticketTypes)) {
                ticketTypes.value = data.ticketTypes

                hotelierTicketTypeIds.value = []
                customerTicketTypeIds.value = []

                ticketTypes.value.forEach((type) => {
                    if (type.name === 'Reservation' || type.name === 'Question') {
                        hotelierTicketTypeIds.value.push(String(type.id))
                        if (type.name === 'Question') {
                            try {
                                localStorage.setItem('questionsTicketTypeId', String(type.id))
                            } catch { /* ignore */ }
                            // Persist to MongoDB
                            persistConfigToMongo('questionsTicketTypeId', String(type.id))
                        }
                    } else if (type.name === 'Customer') {
                        customerTicketTypeIds.value.push(String(type.id))
                    }
                })

                if (ticketTypes.value.length > 0) {
                    try {
                        localStorage.setItem('defaultTicketTypeId', String(ticketTypes.value[0].id))
                    } catch { /* ignore */ }
                    // Persist to MongoDB
                    persistConfigToMongo('defaultTicketTypeId', String(ticketTypes.value[0].id))
                }
            } else {
                console.warn('No ticket types found or invalid response format')
                ticketTypes.value = []
            }
        } catch (error: any) {
            ticketTypesError.value = error.message || 'Failed to load ticket types'
            console.error('Error loading ticket types:', error)
        } finally {
            isLoadingTicketTypes.value = false
        }
    }

    async function ensureTicketTypesExist() {
        isCreatingTicketTypes.value = true
        ticketTypesError.value = null

        try {
            const token = useCookie('token').value
            if (!token) {
                throw new Error('Authentication token not found. Please log in.')
            }

            await loadTicketTypes() // Load existing first

            // Fetch full details to check categories accurately
            const ticketTypesResponse = await fetch('/proxy/tickets/types', {
                headers: {Accept: 'application/json', Token: token},
            })
            if (!ticketTypesResponse.ok) {
                throw new Error(`Failed to get detailed ticket types: ${ticketTypesResponse.status}`)
            }
            const ticketTypesData = await ticketTypesResponse.json()
            const fullTicketTypes = ticketTypesData.ticketTypes || []

            const requiredTypes = [
                {name: 'Reservation', category: 'Customer'},
                {name: 'Customer', category: 'Customer'},
                {name: 'Question', category: 'Customer'}, // Note: Original code had 'Question ' (with space)
            ]

            for (const reqType of requiredTypes) {
                const typeExists = fullTicketTypes.some(
                    (type: TicketType) => type.name === reqType.name && type.category === reqType.category
                )

                if (!typeExists) {
                    const createResponse = await fetch('/proxy/tickets/create-type', {
                        method: 'POST',
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json',
                            Token: token,
                        },
                        body: JSON.stringify({name: reqType.name, category: reqType.category}),
                    })

                    if (!createResponse.ok) {
                        const errorData = await createResponse.json()
                        throw new Error(
                            `Failed to create ${reqType.name} ticket type: ${errorData.message || createResponse.statusText}`
                        )
                    }
                }
            }
            await loadTicketTypes() // Reload to get updated list and IDs

            // Ensure hotelier ticket types have the target_company_id attribute
            await ensureTargetCompanyAttribute(token)
        } catch (err: any) {
            ticketTypesError.value = err.message || 'An error occurred while creating ticket types'
            console.error('Error creating ticket types (Composable):', err)
        } finally {
            isCreatingTicketTypes.value = false
        }
    }

    async function updateAllTicketTypesToCustomer() {
        if (!isTO1User.value) {
            console.warn('Only TO1 users can update ticket types (Composable)')
            nuxtApp.$toast.warning('Only TO1 users can update ticket types')
            return
        }

        isUpdatingTicketTypes.value = true
        ticketTypesError.value = null
        updateResults.value = {success: 0, failed: 0}

        try {
            const token = useCookie('token').value
            if (!token) {
                throw new Error('Authentication token not found. Please log in.')
            }

            await loadTicketTypes() // Load all ticket types first

            if (ticketTypes.value.length === 0) {
                throw new Error('No ticket types found to update')
            }

            for (const ticketType of ticketTypes.value) {
                try {
                    if (ticketType.category === 'Customer') {
                        updateResults.value.success++
                        continue
                    }

                    const response = await fetch(`/proxy/tickets/types/${ticketType.id}`, {
                        method: 'PUT',
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json',
                            Token: token,
                        },
                        body: JSON.stringify({category: 'Customer'}),
                    })

                    if (!response.ok) {
                        let errorMessage = `Error: ${response.status}`
                        try {
                            const errorData = await response.json()
                            errorMessage = errorData.message || errorMessage
                        } catch (e) {
                            /* ignore */
                        }
                        throw new Error(errorMessage)
                    }
                    updateResults.value.success++
                } catch (error: any) {
                    console.error(
                        `Failed to update ticket type ${ticketType.name} (ID: ${ticketType.id}) (Composable):`,
                        error
                    )
                    updateResults.value.failed++
                }
            }

            await loadTicketTypes() // Reload to reflect changes

            if (updateResults.value.success > 0 && updateResults.value.failed === 0) {
                nuxtApp.$toast.success(
                    `Successfully updated ${updateResults.value.success} ticket types to category "Customer"`
                )
            } else if (updateResults.value.success > 0 && updateResults.value.failed > 0) {
                nuxtApp.$toast.warning(
                    `Updated ${updateResults.value.success} ticket types, but failed to update ${updateResults.value.failed} ticket types`
                )
            } else if (updateResults.value.failed > 0) {
                nuxtApp.$toast.error(`Failed to update ${updateResults.value.failed} ticket types`)
            } else {
                nuxtApp.$toast.info('No ticket types required an update.')
            }
        } catch (err: any) {
            ticketTypesError.value = err.message || 'An error occurred while updating ticket types'
            console.error('Error updating ticket types (Composable):', err)
            nuxtApp.$toast.error(ticketTypesError.value)
        } finally {
            isUpdatingTicketTypes.value = false
        }
    }

    async function ensureTargetCompanyAttribute(token: string) {
        const ATTR_NAME = 'target_company_id'

        for (const typeId of hotelierTicketTypeIds.value) {
            try {
                // Fetch full ticket type details to check for existing attribute
                const response = await fetch(`/proxy/tickets/types/${typeId}/attributes`, {
                    headers: {Accept: 'application/json', Token: token},
                })

                if (response.ok) {
                    const data = await response.json()
                    const attrs = data.attributes || []
                    const existing = attrs.find((a: any) => a.name === ATTR_NAME)

                    if (existing) {
                        try {
                            localStorage.setItem(`targetCompanyAttrId_${typeId}`, String(existing.id))
                        } catch { /* ignore */ }
                        // Persist to MongoDB
                        persistConfigToMongo(`targetCompanyAttrId_${typeId}`, String(existing.id))
                        continue
                    }
                }

                // Create the attribute
                const createResponse = await fetch(`/proxy/tickets/types/${typeId}/attributes`, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Token: token,
                    },
                    body: JSON.stringify({
                        name: ATTR_NAME,
                        description: 'Internal Intercom company ID of the target company',
                        data_type: 'string',
                        required_to_create: false,
                        required_to_create_for_contacts: false,
                        visible_on_create: false,
                        visible_to_contacts: false,
                    }),
                })

                if (createResponse.ok) {
                    const created = await createResponse.json()
                    try {
                        localStorage.setItem(`targetCompanyAttrId_${typeId}`, String(created.attributeId))
                    } catch { /* ignore */ }
                    // Persist to MongoDB
                    persistConfigToMongo(`targetCompanyAttrId_${typeId}`, String(created.attributeId))
                } else {
                    console.error(`Failed to create target_company_id attribute on ticket type ${typeId}`)
                }
            } catch (error) {
                console.error(`Error ensuring target_company_id attribute on ticket type ${typeId}:`, error)
            }
        }
    }

    return {
        ticketTypes,
        isLoadingTicketTypes,
        ticketTypesError,
        isCreatingTicketTypes,
        isUpdatingTicketTypes,
        updateResults,
        hotelierTicketTypeIds,
        customerTicketTypeIds,
        isTO1User, // Expose if needed by consuming components, though DashboardTab will get it as prop
        loadTicketTypes,
        ensureTicketTypesExist,
        updateAllTicketTypesToCustomer,
    }
}
